import type { FC } from "react";
import { Menu, ActionIcon, type MenuProps } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDotsVertical, IconTablePlus, IconTrashFilled } from "@tabler/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import { AddTilesetToMapModal } from "../tileset/components/AddTilesetToMapModal.js";
import { DeleteTilesetModal } from "../tileset/components/DeleteTilesetModal.js";
import type { TilesetItemControlsProps } from "../tileset/components/TilesetItemControls.js";
import { stopPropagation } from "../../../utils/stopPropagation.js";

type TilesetMenuProps = TilesetItemControlsProps & MenuProps;

export const TilesetMenu: FC<TilesetMenuProps> = (props) => {
  const { account, tileset, version, dataset, ...menuStylingProps } = props;
  const intl = useIntl();

  const label = intl.formatMessage({
    defaultMessage: "Options",
    id: "label.options",
  });

  const [
    deleteModalOpened, {
      open: openDeleteModal,
      close: closeDeleteModal,
    },
  ] = useDisclosure(false);

  const [
    addToMapModalOpened, {
      open: openAddToMapModal,
      close: closeAddMapModal,
    },
  ] = useDisclosure(false);

  if (version?.status !== "ready" && tileset.scope !== "private") {
    return null;
  }

  return (
    <>
      <Menu {...menuStylingProps}>
        <Menu.Target>
          <ActionIcon aria-label={label} variant="default" h={32}>
            <IconDotsVertical stroke={1.5} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={stopPropagation}>
          {version?.status === "ready" && (
            <Menu.Item
              leftSection={<IconTablePlus size={16} />}
              onClick={openAddToMapModal}
            >
              <FormattedMessage defaultMessage="Add to map" id="label.addToMap" />
            </Menu.Item>
          )}
          {tileset.scope === "private" && (
            <Menu.Item
              leftSection={<IconTrashFilled size={16} />}
              onClick={openDeleteModal}
            >
              <FormattedMessage defaultMessage="Delete" id="label.delete" />
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      {
        tileset.scope === "private" && (
          <DeleteTilesetModal
            open={deleteModalOpened}
            onClose={closeDeleteModal}
            tileset={tileset}
          />
        )
      }
      {
        version?.status === "ready" && (
          <AddTilesetToMapModal
            open={addToMapModalOpened}
            onClose={closeAddMapModal}
            account={account}
            tileset={tileset}
            version={version}
            dataset={dataset}
          />
        )
      }
    </>
  );
};
